import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NotificationService } from '../../services/notification.service';
import { ProdutosV2FacadeService } from '../produtos-v2.facade';
import {
  ChoiceGroupVM,
  ChoiceOptionVM,
  LibraryGroupVM,
  ProductEditorVM
} from '../produtos-v2.models';

type AdicionalOptionModeV2 = 'review' | 'activation' | 'batch-price';
type AdicionalOptionSortV2 = 'name-asc' | 'price-asc' | 'price-desc';
type BatchPriceAdjustMode = 'fixed' | 'increment' | 'percent';
type SaveFooterState = 'hidden' | 'dirty' | 'saving' | 'saved';
type CreationPhase = 'config' | 'full';
type AdicionalWizardMode = 'choice' | 'create' | 'library';
type AdicionalCreateStep = 1 | 2 | 3;
type LibraryHintMode = 'link' | 'duplicate';
type AdicionalSectionKey = 'identity' | 'rules' | 'options' | 'summary';

@Component({
  selector: 'app-tela-editor-adicional-v2',
  templateUrl: './tela-editor-adicional-v2.component.html',
  styleUrls: ['./tela-editor-adicional-v2.component.scss']
})
export class TelaEditorAdicionalV2Component implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() editor!: ProductEditorVM;
  @Input() empresa: any;
  @Input() podeEditar = true;
  @Input() podeEditarPrecos = true;
  @Input() podeEditarDisponibilidade = true;
  @Input() groupId: number | string | null = null;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() startWithLibrary = false;

  @Output() closeRequested = new EventEmitter<void>();
  @Output() groupSaved = new EventEmitter<ChoiceGroupVM>();
  @Output() groupDeleted = new EventEmitter<ChoiceGroupVM>();
  @Output() groupDuplicated = new EventEmitter<void>();
  @Output() libraryRequested = new EventEmitter<void>();
  @Output() actionBarStateChange = new EventEmitter<{
    creationPhase: CreationPhase;
    saveFooterState: SaveFooterState;
  }>();

  groupDraft: ChoiceGroupVM | null = null;
  private snapshot = '';
  isDirty = false;
  saveFooterState: SaveFooterState = 'hidden';
  creationPhase: CreationPhase = 'config';
  wizardMode: AdicionalWizardMode = 'choice';
  createStep: AdicionalCreateStep = 1;
  choiceSelection: 'create' | 'library' = 'create';
  isMobile = false;
  groupFormCollapsedMobile = false;
  isEmbeddedContext = false;
  librarySearch = '';
  libraryFilter = 'Todos';
  libraryLoading = false;
  libraryItems: LibraryGroupVM[] = [];
  selectedLibraryGroup: LibraryGroupVM | null = null;
  libraryHintMode: LibraryHintMode = 'link';
  libraryMobilePreviewExpanded = false;
  sectionExpanded: Record<AdicionalSectionKey, boolean> = {
    identity: true,
    rules: true,
    options: true,
    summary: false
  };

  // Option browser state
  optionMode: AdicionalOptionModeV2 = 'review';
  optionSearch = '';
  optionSort: AdicionalOptionSortV2 = 'name-asc';

  // Add option
  newOptionName = '';
  newOptionPrice = '';

  // Edit sheet
  editSheetOpen = false;
  editingOptionId: string | number | null = null;
  editingOptionName = '';
  editingOptionPrice: number | null = null;
  editingOptionPdv = '';
  editingOptionDescription = '';
  editingOptionLinkImagem: string | null = null;
  editingOptionQtdeMaxima: number | null = null;
  editingOptionUploadingImage = false;
  editSheetAdvancedOpen = false;

  // Batch
  batchSelection: Record<string, boolean> = {};
  batchSheetOpen = false;
  batchAdjustMode: BatchPriceAdjustMode = 'fixed';
  batchAdjustValue = '';

  // Delete dialog
  showDeleteDialog = false;

  // Edit hint
  showEditHint = true;

  // Private
  private optionRuntimeKeys = new WeakMap<ChoiceOptionVM, string>();
  private optionRuntimeKeySequence = 0;
  private savedTimer: any = null;
  private groupSaveTimer: any = null;

  constructor(
    private facade: ProdutosV2FacadeService,
    private notificationService: NotificationService,
    private elementRef: ElementRef,
    private httpClient: HttpClient
  ) {
    this.handleResize();
  }

  ngOnInit(): void {
    this.initDraft();
  }

  ngAfterViewInit(): void {
    this.updateEmbeddedContext();
    this.scrollToTop();
  }

  private updateEmbeddedContext(): void {
    const el = this.elementRef.nativeElement as HTMLElement;
    this.isEmbeddedContext = !!el.closest('.pv2-editor-page.is-embedded');
  }

  private emitActionBarState(): void {
    this.actionBarStateChange.emit({
      creationPhase: this.creationPhase,
      saveFooterState: this.saveFooterState
    });
  }

  private scrollToTop(): void {
    const el = this.elementRef.nativeElement as HTMLElement;
    const scrollParent = el.closest('.pv2-editor-drawer')
      || el.closest('.pv2-editor-page')
      || el.closest('.pv2-editor-canvas');
    if (scrollParent) {
      scrollParent.scrollTop = 0;
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['groupId'] || changes['mode'] || changes['editor'] || changes['startWithLibrary']) {
      this.initDraft();
      setTimeout(() => this.scrollToTop(), 0);
    }
  }

  ngOnDestroy(): void {
    if (this.savedTimer) clearTimeout(this.savedTimer);
    if (this.groupSaveTimer) clearTimeout(this.groupSaveTimer);
  }

  @HostListener('window:resize')
  handleResize() {
    this.isMobile = window.innerWidth < 1024;
  }

  // ────────────────────────────── Getters ──────────────────────────────

  get focusedGroup(): ChoiceGroupVM | null {
    if (!this.editor || this.groupId == null) return null;
    return this.editor.groups.find((g) => String(g.id) === String(this.groupId)) || null;
  }

  get isActivationMode(): boolean {
    return this.optionMode === 'activation';
  }

  get isReviewMode(): boolean {
    return this.optionMode === 'review';
  }

  get isBatchPriceMode(): boolean {
    return this.optionMode === 'batch-price';
  }

  get modeThemeClass(): string {
    switch (this.optionMode) {
      case 'activation': return 'is-mode-onoff';
      case 'review': return 'is-mode-editar';
      case 'batch-price': return 'is-mode-lote';
      default: return '';
    }
  }

  get groupActiveCount(): number {
    const group = this.groupDraft;
    return (group?.options || []).filter((o) => o.active !== false).length;
  }

  get groupTotalCount(): number {
    return this.groupDraft?.options?.length || 0;
  }

  get batchSelectedCount(): number {
    return Object.keys(this.batchSelection).filter((key) => this.batchSelection[key]).length;
  }

  get filteredOptions(): ChoiceOptionVM[] {
    const group = this.groupDraft;
    if (!group) return [];

    const term = this.optionSearch.trim().toLowerCase();
    const filtered = (group.options || []).filter((option) => {
      return !term || (option.name || '').toLowerCase().includes(term);
    });

    return filtered.sort((left, right) => {
      if (this.optionSort === 'price-asc') {
        return Number(left.price || 0) - Number(right.price || 0)
          || String(left.name || '').localeCompare(String(right.name || ''), 'pt-BR');
      }
      if (this.optionSort === 'price-desc') {
        return Number(right.price || 0) - Number(left.price || 0)
          || String(left.name || '').localeCompare(String(right.name || ''), 'pt-BR');
      }
      return String(left.name || '').localeCompare(String(right.name || ''), 'pt-BR');
    });
  }

  get showSearchAndSort(): boolean {
    return (this.groupDraft?.options?.length || 0) > 2;
  }

  get hasOptions(): boolean {
    return (this.groupDraft?.options?.length || 0) > 0;
  }

  get canContinue(): boolean {
    return this.podeEditar && !!this.groupDraft?.name?.trim();
  }

  get isChoiceMode(): boolean {
    return this.wizardMode === 'choice';
  }

  get isCreateMode(): boolean {
    return this.wizardMode === 'create';
  }

  get isLibraryMode(): boolean {
    return this.wizardMode === 'library';
  }

  get canContinueChoice(): boolean {
    return !!this.choiceSelection;
  }

  get canAdvanceCreateStep(): boolean {
    if (this.createStep === 1) {
      return this.canContinue;
    }

    if (this.createStep === 2) {
      return this.hasOptions;
    }

    return true;
  }

  get filteredLibraryItems(): LibraryGroupVM[] {
    const term = this.librarySearch.trim().toLowerCase();
    return this.libraryItems.filter((item) => {
      const matchesSearch = !term || item.name.toLowerCase().includes(term);
      if (!matchesSearch) return false;
      if (this.libraryFilter === 'Mais usados') return item.sharedCount > 1;
      if (this.libraryFilter === 'Recentes') return item.sharedCount <= 1;
      return true;
    });
  }

  get editorPlaceholder(): string {
    if (!this.editor) return '\uD83C\uDF7D\uFE0F';
    return this.editor.emoji || (this.editor.type === 'pizza' ? '\uD83C\uDF55' : '\uD83C\uDF7D\uFE0F');
  }

  get headerTitle(): string {
    if (this.isCreateMode && this.mode === 'edit') {
      return 'Editar adicional';
    }
    return this.groupDraft?.name || 'Novo adicional';
  }

  get headerSubtitle(): string {
    const parts: string[] = [];
    if (this.editor?.name) parts.push(this.editor.name);
    if (this.editor?.category) parts.push(this.editor.category);
    if (this.groupDraft && this.groupDraft.options.length) {
      parts.push(`${this.groupDraft.options.length} opções`);
    }
    return parts.join(' • ');
  }

  get heroStatusLabel(): string {
    if (this.saveFooterState === 'saving') return 'Salvando...';
    if (this.saveFooterState === 'saved') return 'Salvo';
    if (this.isDirty) return 'Alterações pendentes';
    if (this.mode === 'create') return 'Rascunho';
    return 'Salvo';
  }

  get podeEditarAlgumaOpcao(): boolean {
    return this.podeEditar || this.podeEditarPrecos || this.podeEditarDisponibilidade;
  }

  get previewTitle(): string {
    if (this.isLibraryMode && this.selectedLibraryGroup) {
      return this.selectedLibraryGroup.name || 'Adicional';
    }
    return this.groupDraft?.name?.trim() || 'Novo adicional';
  }

  get previewRequired(): boolean {
    if (this.isLibraryMode && this.selectedLibraryGroup) {
      return !!this.selectedLibraryGroup.required;
    }
    return !!this.groupDraft?.required;
  }

  get previewSelectionText(): string {
    const type = this.isLibraryMode && this.selectedLibraryGroup
      ? this.selectedLibraryGroup.type
      : this.groupDraft?.type;
    const min = this.isLibraryMode && this.selectedLibraryGroup
      ? (this.selectedLibraryGroup.min ?? 0)
      : (this.groupDraft?.min ?? 0);
    const max = this.isLibraryMode && this.selectedLibraryGroup
      ? (this.selectedLibraryGroup.max ?? 1)
      : (this.groupDraft?.max ?? 1);

    if (type === 'single') {
      return `Escolha de ${Math.max(min, 1)} até 1`;
    }

    return `Escolha de ${min} até ${max}`;
  }

  get previewOptions(): string[] {
    if (this.isLibraryMode && this.selectedLibraryGroup) {
      return this.selectedLibraryGroup.previewOptions || [];
    }

    return (this.groupDraft?.options || [])
      .filter((option) => option.active !== false)
      .map((option) => option.name)
      .filter((name) => !!name)
      .slice(0, 3);
  }

  get previewHintTitle(): string {
    if (!this.isLibraryMode) {
      return this.hasSharedUsageInOtherProducts ? 'Compartilhado' : 'Preview';
    }

    return this.libraryHintMode === 'duplicate' ? 'Cópia independente' : 'Dica';
  }

  get previewHintText(): string {
    if (!this.isLibraryMode) {
      if (this.hasSharedUsageInOtherProducts) {
        return this.getSharedWarningSupportText();
      }
      return 'A visualização reage em tempo real e mostra como este adicional aparece para o cliente.';
    }

    if (this.libraryHintMode === 'duplicate') {
      return 'Ao duplicar, este produto recebe uma cópia independente. As edições não afetam o adicional original.';
    }

    return 'Ao vincular, as alterações neste adicional serão refletidas em todos os produtos que o utilizam.';
  }

  get libraryMobilePreviewLabel(): string {
    return this.selectedLibraryGroup?.name?.trim() || 'Selecione um adicional para visualizar';
  }

  get sharedWarningSupportText(): string {
    return this.getSharedWarningSupportText();
  }

  get hasSharedUsageInOtherProducts(): boolean {
    return !!this.groupDraft?.shared && Number(this.groupDraft?.sharedCount || 0) > 1;
  }

  get deleteDialogTitle(): string {
    return this.groupDraft?.shared ? 'Remover adicional deste produto?' : 'Excluir adicional?';
  }

  get deleteDialogBody(): string {
    if (this.groupDraft?.shared) {
      return 'Esse adicional está compartilhado. Ao remover, ele será desvinculado apenas deste produto.';
    }

    return 'Essa ação é irreversível. Todas as opções vinculadas a este adicional serão removidas permanentemente.';
  }

  get deleteActionLabel(): string {
    return this.groupDraft?.shared ? 'Remover deste produto' : 'Excluir';
  }

  get identitySectionSummary(): string {
    const name = this.groupDraft?.name?.trim() || 'Sem nome';
    const type = this.getTypeLabel(this.groupDraft?.type);
    return `${name} • ${type}`;
  }

  get rulesSectionSummary(): string {
    const required = this.groupDraft?.required ? 'Obrigatório' : 'Opcional';
    const quantity = this.groupDraft?.type === 'single'
      ? '1 seleção'
      : `${this.groupDraft?.min ?? 0}–${this.groupDraft?.max ?? 1}`;
    return `${required} • ${quantity} • ${this.getChargeTypeLabel(this.groupDraft?.chargeType)}`;
  }

  get optionsSectionSummary(): string {
    const total = this.groupDraft?.options?.length || 0;
    return `${total} ${total === 1 ? 'opção' : 'opções'}`;
  }

  get summarySectionSummary(): string {
    if (!this.groupDraft?.shared) {
      return 'Só neste produto';
    }

    const sharedCount = Number(this.groupDraft?.sharedCount || 0);
    if (sharedCount > 1) {
      return `Compartilhado em ${sharedCount} produtos`;
    }

    return 'Compartilhado';
  }

  // ────────────────────────────── Init ──────────────────────────────

  private initDraft(): void {
    if (this.mode === 'edit' && this.groupId != null) {
      const group = this.focusedGroup;
      if (group) {
        this.groupDraft = this.cloneGroup(group);
        this.creationPhase = 'full';
        this.wizardMode = 'create';
        this.createStep = 1;
      } else {
        this.groupDraft = this.buildEmptyGroupDraft();
        this.creationPhase = 'config';
        this.wizardMode = this.startWithLibrary ? 'library' : 'choice';
        this.createStep = 1;
      }
    } else {
      this.groupDraft = this.buildEmptyGroupDraft();
      this.creationPhase = 'config';
      this.wizardMode = this.startWithLibrary ? 'library' : 'choice';
      this.createStep = 1;
    }

    this.choiceSelection = this.startWithLibrary ? 'library' : 'create';
    this.librarySearch = '';
    this.libraryFilter = 'Todos';
    this.libraryHintMode = 'link';
    this.selectedLibraryGroup = null;
    this.libraryMobilePreviewExpanded = false;
    if (this.isLibraryMode) {
      void this.loadLibrary();
    }

    this.takeSnapshot();
    this.isDirty = false;
    this.saveFooterState = 'hidden';
    this.optionMode = 'review';
    this.optionSearch = '';
    this.optionSort = 'name-asc';
    this.batchSelection = {};
    this.editSheetOpen = false;
    this.batchSheetOpen = false;
    this.showDeleteDialog = false;
    this.newOptionName = '';
    this.newOptionPrice = '';
    this.resetSectionExpansion();
    this.emitActionBarState();
  }

  private buildEmptyGroupDraft(): ChoiceGroupVM {
    return {
      id: null,
      name: '',
      type: 'single',
      required: false,
      min: 0,
      max: 1,
      chargeType: 'soma',
      shared: false,
      sharedCount: 0,
      order: this.editor?.groups?.length || 0,
      kind: 'adicional',
      description: 'Escolha 1 • 0 opções',
      options: []
    };
  }

  private cloneGroup(grupo: ChoiceGroupVM): ChoiceGroupVM {
    return JSON.parse(JSON.stringify(grupo));
  }

  private getSharedWarningSupportText(): string {
    const sharedCount = Number(this.groupDraft?.sharedCount || 0);
    if (sharedCount > 1) {
      return `Em uso em ${sharedCount} produtos.`;
    }

    return 'Editar aqui afeta outros produtos.';
  }

  private resetSectionExpansion(): void {
    this.sectionExpanded = {
      identity: true,
      rules: true,
      options: true,
      summary: false
    };
  }

  private getTypeLabel(type?: string | null): string {
    if (type === 'multipleRepeat') return 'Múltipla c/ rep.';
    if (type === 'multiple') return 'Múltipla';
    return 'Única';
  }

  private getChargeTypeLabel(type?: string | null): string {
    if (type === 'media') return 'Média';
    if (type === 'maior') return 'Maior';
    return 'Soma';
  }

  private takeSnapshot(): void {
    this.snapshot = JSON.stringify(this.groupDraft);
  }

  // ────────────────────────────── Dirty State ──────────────────────────────

  checkDirty(): void {
    const currentState = JSON.stringify(this.groupDraft);
    this.isDirty = currentState !== this.snapshot;

    if (this.creationPhase !== 'full' || this.mode === 'create') {
      this.saveFooterState = 'hidden';
      this.emitActionBarState();
      return;
    }

    if (this.isDirty && this.saveFooterState !== 'saving') {
      this.saveFooterState = 'dirty';
    } else if (!this.isDirty && this.saveFooterState === 'dirty') {
      this.saveFooterState = 'hidden';
    }

    this.emitActionBarState();
  }

  onFieldChange(): void {
    if (!this.podeEditar) return;
    this.checkDirty();
  }

  isSectionExpanded(section: AdicionalSectionKey): boolean {
    return !!this.sectionExpanded[section];
  }

  toggleSection(section: AdicionalSectionKey): void {
    this.sectionExpanded[section] = !this.sectionExpanded[section];
  }

  // ────────────────────────────── Progressive Disclosure ──────────────────────────────

  async onContinue(): Promise<void> {
    if (this.isChoiceMode) {
      if (this.choiceSelection === 'library') {
        await this.enterLibraryMode();
        return;
      }

      this.wizardMode = 'create';
      this.creationPhase = 'full';
      this.createStep = 1;
      this.emitActionBarState();
      return;
    }

    if (this.isCreateMode && this.createStep < 3) {
      this.goToNextStep();
      return;
    }

    if (!this.podeEditar) {
      this.notificationService.showError('A estrutura do adicional está bloqueada neste catálogo compartilhado.');
      return;
    }
    if (!this.canContinue || !this.groupDraft) return;
    this.creationPhase = 'full';
    this.takeSnapshot();
    this.isDirty = false;
    this.saveFooterState = 'hidden';
    this.emitActionBarState();
  }

  // ────────────────────────────── Save / Cancel ──────────────────────────────

  async onSave(): Promise<void> {
    if (!this.podeEditar) {
      this.notificationService.showError('A estrutura do adicional está bloqueada neste catálogo compartilhado.');
      return;
    }
    if (!this.groupDraft || !this.editor) return;
    if (!this.hasOptions) {
      this.notificationService.showError('Adicione pelo menos 1 opção antes de salvar o adicional.');
      return;
    }

    try {
      this.saveFooterState = 'saving';
      this.emitActionBarState();

      if (!this.editor.raw?.id) {
        this.notificationService.showError('Salve o produto antes de salvar o adicional.');
        this.saveFooterState = 'dirty';
        this.emitActionBarState();
        return;
      }

      this.groupDraft.description = this.describeDraft(this.groupDraft);

      await this.facade.salveGrupo(this.empresa.catalogo, this.editor.raw, this.groupDraft);

      if (this.groupDraft.kind === 'adicional' && this.groupDraft.raw) {
        this.editor.raw.camposAdicionais = this.editor.raw.camposAdicionais || [];
        const existing = (this.editor.raw.camposAdicionais || []).find((item: any) => item.id === this.groupDraft!.raw?.id);
        if (!existing) {
          this.editor.raw.camposAdicionais.push(this.groupDraft.raw);
        }
      }

      this.takeSnapshot();
      this.isDirty = false;
      this.saveFooterState = 'saved';
      this.emitActionBarState();
      this.groupSaved.emit(this.groupDraft);

      if (this.savedTimer) clearTimeout(this.savedTimer);
      this.savedTimer = setTimeout(() => {
        this.saveFooterState = 'hidden';
        this.emitActionBarState();
      }, 2000);
    } catch (error: any) {
      this.saveFooterState = 'dirty';
      this.emitActionBarState();
      this.notificationService.showError(error?.message || 'Não foi possível salvar o adicional.');
    }
  }

  async onSaveAndClose(): Promise<void> {
    await this.onSave();
    if (this.saveFooterState === 'saved') {
      this.closeRequested.emit();
    }
  }

  onCancel(): void {
    if (!this.snapshot) return;
    this.groupDraft = JSON.parse(this.snapshot);
    this.isDirty = false;
    this.saveFooterState = 'hidden';
    this.emitActionBarState();
  }

  // ────────────────────────────── Delete ──────────────────────────────

  requestDelete(): void {
    if (!this.podeEditar) {
      this.notificationService.showError('A exclusão do adicional está bloqueada neste catálogo compartilhado.');
      return;
    }
    this.showDeleteDialog = true;
  }

  cancelDelete(): void {
    this.showDeleteDialog = false;
  }

  async confirmDelete(): Promise<void> {
    if (!this.podeEditar) {
      this.notificationService.showError('A exclusão do adicional está bloqueada neste catálogo compartilhado.');
      return;
    }
    if (!this.groupDraft || !this.editor) return;

    try {
      this.showDeleteDialog = false;
      await this.facade.removaGrupo(this.editor.raw, this.empresa.catalogo, this.groupDraft);
      this.editor.raw.camposAdicionais = (this.editor.raw.camposAdicionais || [])
        .filter((item: any) => item.id !== this.groupDraft!.raw?.id);
      this.groupDeleted.emit(this.groupDraft);
      this.notificationService.showSuccess(this.groupDraft.shared ? 'Adicional removido deste produto.' : 'Adicional excluído.');
      this.closeRequested.emit();
    } catch (error: any) {
      this.notificationService.showError(error?.message || 'Não foi possível excluir o adicional.');
    }
  }

  // ────────────────────────────── Option Modes ──────────────────────────────

  setOptionMode(mode: AdicionalOptionModeV2): void {
    if (mode === 'review' && !this.podeEditarAlgumaOpcao) return;
    if (mode === 'activation' && !this.podeEditarDisponibilidade) return;
    if (mode === 'batch-price' && !this.podeEditarPrecos) return;
    const previousMode = this.optionMode;
    this.optionMode = mode;
    if (previousMode === 'batch-price' && mode !== 'batch-price') {
      this.clearBatchSelection();
      this.closeBatchSheet();
    }
    if (mode !== 'review') {
      this.editSheetOpen = false;
      this.commitEditingOption();
    }
  }

  setOptionSearch(value: string): void {
    this.optionSearch = value;
  }

  setOptionSort(sort: AdicionalOptionSortV2): void {
    this.optionSort = sort;
  }

  // ────────────────────────────── Add Option ──────────────────────────────

  addOptionFromInput(): void {
    if (!this.podeEditar) {
      this.notificationService.showError('A inclusão de opções está bloqueada neste catálogo compartilhado.');
      return;
    }
    if (!this.groupDraft) return;

    const name = this.newOptionName.trim();
    if (!name) return;

    const price = this.parseOptionMoney(this.newOptionPrice);

    const hadNoOptions = this.groupDraft.options.length === 0;

    const option: ChoiceOptionVM = {
      id: null,
      name,
      price,
      active: true,
      order: this.groupDraft.options.length,
      raw: { nome: name, valor: price, disponivel: true, nova: true }
    };

    this.groupDraft.options.push(option);
    if (!this.groupDraft.raw) {
      this.groupDraft.raw = {};
    }
    if (!this.groupDraft.raw.opcoesDisponiveis) {
      this.groupDraft.raw.opcoesDisponiveis = [];
    }
    this.groupDraft.raw.opcoesDisponiveis.push(option.raw);

    this.newOptionName = '';
    this.newOptionPrice = '';
    if (hadNoOptions) {
      this.sectionExpanded.options = true;
    }
    this.checkDirty();

    if (this.mode === 'edit') {
      this.scheduleAutoSave();
    }
  }

  // ────────────────────────────── Chip Actions ──────────────────────────────

  onChipClickOnOff(option: ChoiceOptionVM): void {
    if (!this.podeEditarDisponibilidade) {
      this.notificationService.showError('A disponibilidade da opção está bloqueada neste catálogo compartilhado.');
      return;
    }
    option.active = !option.active;
    this.syncOptionToRaw(option);
    this.checkDirty();
    if (this.mode === 'edit') {
      this.scheduleAutoSave();
    }
  }

  onChipClickEditar(option: ChoiceOptionVM): void {
    if (!this.podeEditarAlgumaOpcao) {
      this.notificationService.showError('A edição das opções está bloqueada neste catálogo compartilhado.');
      return;
    }
    this.editingOptionId = option.id ?? option.name;
    this.editingOptionName = option.name;
    this.editingOptionPrice = option.price;
    this.editingOptionPdv = option.codigoPdv || '';
    this.editingOptionDescription = option.description || '';
    this.editingOptionLinkImagem = option.linkImagem || null;
    this.editingOptionQtdeMaxima = option.qtdeMaxima ?? null;
    this.editSheetAdvancedOpen = !this.isMobile || this.hasAdvancedOptionData();
    this.editSheetOpen = true;
  }

  onChipClickLote(option: ChoiceOptionVM): void {
    if (!this.podeEditarPrecos) {
      this.notificationService.showError('O preço em massa está bloqueado neste catálogo compartilhado.');
      return;
    }
    this.toggleBatchOption(option);
  }

  isEditingOption(option: ChoiceOptionVM): boolean {
    return this.editingOptionId === (option.id ?? option.name);
  }

  // ────────────────────────────── Edit Sheet ──────────────────────────────

  closeEditSheet(): void {
    this.editSheetOpen = false;
    this.editingOptionId = null;
    this.editingOptionName = '';
    this.editingOptionPrice = null;
    this.editingOptionPdv = '';
    this.editingOptionDescription = '';
    this.editingOptionLinkImagem = null;
    this.editingOptionQtdeMaxima = null;
    this.editingOptionUploadingImage = false;
    this.editSheetAdvancedOpen = false;
  }

  hasAdvancedOptionData(): boolean {
    return !!(this.editingOptionLinkImagem || this.editingOptionDescription || this.editingOptionQtdeMaxima);
  }

  saveAndCloseEditSheet(): void {
    if (!this.podeEditarAlgumaOpcao) {
      this.closeEditSheet();
      return;
    }
    this.commitEditingOption();
    this.editSheetOpen = false;
  }

  deleteAndCloseEditSheet(): void {
    if (!this.podeEditar) {
      this.notificationService.showError('A exclusão de opções está bloqueada neste catálogo compartilhado.');
      return;
    }
    if (!this.groupDraft) return;
    const option = this.groupDraft.options.find(
      (o) => (o.id ?? o.name) === this.editingOptionId
    );
    if (option) {
      this.removeOption(option);
    }
    this.closeEditSheet();
  }

  private commitEditingOption(): void {
    if (this.editingOptionId == null || !this.groupDraft) return;
    const option = this.groupDraft.options.find(
      (o) => (o.id ?? o.name) === this.editingOptionId
    );
    if (option) {
      let changed = false;

      if (this.podeEditar) {
        option.name = this.editingOptionName;
        option.codigoPdv = this.editingOptionPdv || '';
        option.description = this.editingOptionDescription;
        option.linkImagem = this.editingOptionLinkImagem;
        option.qtdeMaxima = this.editingOptionQtdeMaxima;
        changed = true;
      }

      if (this.podeEditarPrecos) {
        option.price = this.editingOptionPrice ?? 0;
        changed = true;
      }

      if (changed) {
        this.syncOptionToRaw(option);
        this.checkDirty();
        if (this.mode === 'edit') {
          this.scheduleAutoSave();
        }
      }
    }
    this.editingOptionId = null;
    this.editingOptionName = '';
    this.editingOptionPrice = null;
    this.editingOptionPdv = '';
    this.editingOptionDescription = '';
    this.editingOptionLinkImagem = null;
    this.editingOptionQtdeMaxima = null;
  }

  // ────────────────────────────── Batch ──────────────────────────────

  isBatchOptionSelected(option: ChoiceOptionVM): boolean {
    return !!this.batchSelection[this.getOptionRuntimeKey(option)];
  }

  toggleBatchOption(option: ChoiceOptionVM): void {
    const key = this.getOptionRuntimeKey(option);
    if (this.batchSelection[key]) {
      delete this.batchSelection[key];
    } else {
      this.batchSelection[key] = true;
    }
  }

  selectAllFilteredBatchOptions(): void {
    if (!this.podeEditarPrecos) return;
    this.filteredOptions.forEach((option) => {
      this.batchSelection[this.getOptionRuntimeKey(option)] = true;
    });
  }

  clearBatchSelection(): void {
    this.batchSelection = {};
  }

  openBatchSheet(): void {
    if (!this.podeEditarPrecos) {
      this.notificationService.showError('O preço em massa está bloqueado neste catálogo compartilhado.');
      return;
    }
    if (!this.batchSelectedCount) {
      this.notificationService.showError('Selecione pelo menos uma opção antes de alterar o preço.');
      return;
    }
    this.batchSheetOpen = true;
    this.batchAdjustMode = 'fixed';
    this.batchAdjustValue = '';
  }

  closeBatchSheet(): void {
    this.batchSheetOpen = false;
    this.batchAdjustValue = '';
  }

  setBatchAdjustMode(mode: BatchPriceAdjustMode): void {
    this.batchAdjustMode = mode;
    this.batchAdjustValue = '';
  }

  applyBatchAdjust(): void {
    if (!this.podeEditarPrecos) {
      this.notificationService.showError('O preço em massa está bloqueado neste catálogo compartilhado.');
      return;
    }
    if (!this.groupDraft || !this.batchSelectedCount) return;

    const rawValue = this.parseOptionMoney(this.batchAdjustValue);

    (this.groupDraft.options || [])
      .filter((option) => this.isBatchOptionSelected(option))
      .forEach((option) => {
        let newPrice: number;
        switch (this.batchAdjustMode) {
          case 'fixed':
            newPrice = rawValue;
            break;
          case 'increment':
            newPrice = Math.max(0, Number(option.price || 0) + rawValue);
            break;
          case 'percent':
            newPrice = Math.max(0, Number(option.price || 0) * (1 + rawValue / 100));
            newPrice = Math.round(newPrice * 100) / 100;
            break;
          default:
            newPrice = rawValue;
        }
        option.price = newPrice;
        this.syncOptionToRaw(option);
      });

    this.closeBatchSheet();
    this.checkDirty();
    if (this.mode === 'edit') {
      this.scheduleAutoSave();
    }
    this.notificationService.showSuccess('Preço ajustado em lote.');
  }

  applyBatchAvailability(active: boolean): void {
    if (!this.podeEditarDisponibilidade) {
      this.notificationService.showError('A disponibilidade da opção está bloqueada neste catálogo compartilhado.');
      return;
    }
    if (!this.groupDraft || !this.batchSelectedCount) {
      this.notificationService.showError('Selecione pelo menos uma opção.');
      return;
    }

    (this.groupDraft.options || [])
      .filter((option) => this.isBatchOptionSelected(option))
      .forEach((option) => {
        option.active = active;
        this.syncOptionToRaw(option);
      });

    this.checkDirty();
    if (this.mode === 'edit') {
      this.scheduleAutoSave();
    }
    this.notificationService.showSuccess(active ? 'Opções ativadas em lote.' : 'Opções desativadas em lote.');
  }

  // ────────────────────────────── Helpers ──────────────────────────────

  trackByOption(index: number, option: ChoiceOptionVM): any {
    return option.id || index;
  }

  dismissEditHint(): void {
    this.showEditHint = false;
  }

  goBack(): void {
    this.closeRequested.emit();
  }

  async enterLibraryMode(): Promise<void> {
    if (!this.podeEditar) {
      this.notificationService.showError('A biblioteca está bloqueada neste catálogo compartilhado.');
      return;
    }

    this.wizardMode = 'library';
    this.creationPhase = 'config';
    await this.loadLibrary();
  }

  async openLibrary(): Promise<void> {
    await this.enterLibraryMode();
  }

  selectChoice(selection: 'create' | 'library'): void {
    this.choiceSelection = selection;
  }

  goToPreviousStep(): void {
    if (this.isLibraryMode) {
      this.wizardMode = 'choice';
      this.creationPhase = 'config';
      return;
    }

    if (!this.isCreateMode) {
      this.wizardMode = 'choice';
      this.creationPhase = 'config';
      return;
    }

    if (this.createStep > 1) {
      this.createStep = (this.createStep - 1) as AdicionalCreateStep;
      return;
    }

    if (this.mode === 'create') {
      this.wizardMode = 'choice';
      this.creationPhase = 'config';
      return;
    }

    this.closeRequested.emit();
  }

  goToStep(step: AdicionalCreateStep): void {
    if (this.mode === 'create') {
      if (step === 2 && !this.canContinue) {
        this.notificationService.showError('Preencha o nome do adicional antes de continuar.');
        return;
      }

      if (step === 3 && !this.hasOptions) {
        this.notificationService.showError('Adicione pelo menos 1 opção antes de continuar.');
        return;
      }
    }

    this.wizardMode = 'create';
    this.creationPhase = 'full';
    this.createStep = step;
    this.emitActionBarState();
  }

  goToNextStep(): void {
    if (this.createStep === 1 && !this.canContinue) {
      this.notificationService.showError('Preencha o nome do adicional para continuar.');
      return;
    }

    if (this.createStep === 2 && !this.hasOptions) {
      this.notificationService.showError('Adicione pelo menos 1 opção para continuar.');
      return;
    }

    this.goToStep(Math.min(3, this.createStep + 1) as AdicionalCreateStep);
  }

  async onLibrarySearchChange(value: string): Promise<void> {
    this.librarySearch = value;
    await this.loadLibrary();
  }

  setLibraryFilter(filter: string): void {
    this.libraryFilter = filter;
    if (this.selectedLibraryGroup && !this.filteredLibraryItems.some((item) => String(item.id) === String(this.selectedLibraryGroup?.id))) {
      this.selectedLibraryGroup = null;
      this.libraryMobilePreviewExpanded = false;
    }
  }

  selectLibraryGroup(group: LibraryGroupVM): void {
    this.selectedLibraryGroup = group;
  }

  toggleLibraryMobilePreview(): void {
    this.libraryMobilePreviewExpanded = !this.libraryMobilePreviewExpanded;
  }

  isLibraryGroupSelected(group: LibraryGroupVM): boolean {
    return String(this.selectedLibraryGroup?.id ?? '') === String(group?.id ?? '');
  }

  isLibraryGroupLinked(item: LibraryGroupVM): boolean {
    if (!this.editor?.groups) return false;
    return this.editor.groups.some(g => String(g.id) === String(item.id));
  }

  setLibraryHintMode(mode: LibraryHintMode): void {
    this.libraryHintMode = mode;
  }

  async useLibraryGroup(group: LibraryGroupVM, clonar: boolean): Promise<void> {
    if (!this.podeEditar) {
      this.notificationService.showError('A biblioteca está bloqueada neste catálogo compartilhado.');
      return;
    }
    if (!this.editor) return;

    const persisted = await this.ensureProductPersisted();
    if (!persisted) return;

    try {
      await this.facade.vinculeGrupoDaBiblioteca(this.empresa.catalogo, this.editor.raw, group, clonar);
      this.notificationService.showSuccess(clonar ? 'Adicional duplicado da biblioteca.' : 'Adicional vinculado da biblioteca.');
      if (clonar) {
        this.groupDuplicated.emit();
      } else {
        this.groupSaved.emit(this.groupDraft || this.buildEmptyGroupDraft());
      }
    } catch (error: any) {
      this.notificationService.showError(error?.message || 'Não foi possível usar o adicional da biblioteca.');
    }
  }

  private async loadLibrary(): Promise<void> {
    if (!this.empresa?.catalogo?.id) return;

    this.libraryLoading = true;
    try {
      this.libraryItems = await this.facade.carregueBiblioteca(this.empresa.catalogo, this.librarySearch, this.editor?.raw || this.editor || null);
      if (this.selectedLibraryGroup && !this.filteredLibraryItems.some((item) => String(item.id) === String(this.selectedLibraryGroup?.id))) {
        this.selectedLibraryGroup = null;
        this.libraryMobilePreviewExpanded = false;
      }
    } finally {
      this.libraryLoading = false;
    }
  }

  private async ensureProductPersisted(): Promise<boolean> {
    if (this.editor?.raw?.id) {
      this.editor.id = this.editor.raw.id;
      return true;
    }

    try {
      await this.facade.salveProduto(this.editor.raw);
      this.editor.id = this.editor.raw?.id || this.editor.id;
      return !!this.editor.raw?.id;
    } catch (error: any) {
      this.notificationService.showError(error?.message || 'Salve o produto antes de usar a biblioteca.');
      return false;
    }
  }

  // ────────────────────────────── Option Image ──────────────────────────────

  onOptionImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    this.editingOptionUploadingImage = true;
    const formData = new FormData();
    formData.append('image', file);

    this.httpClient.post<any>('/upload/imagens/crop/400', formData).subscribe(
      (response) => {
        this.editingOptionLinkImagem = response?.file || response?.data?.file || null;
        this.editingOptionUploadingImage = false;
      },
      () => {
        this.notificationService.showError('Erro ao fazer upload da imagem.');
        this.editingOptionUploadingImage = false;
      }
    );

    input.value = '';
  }

  removeOptionImage(): void {
    this.editingOptionLinkImagem = null;
  }

  resolveOptionImageUrl(linkImagem: string): string {
    if (!linkImagem) return '';
    if (/^https?:\/\//i.test(linkImagem)) return linkImagem;
    return '/images/empresa/' + linkImagem.replace(/^\/+/, '');
  }

  private syncOptionToRaw(option: ChoiceOptionVM): void {
    if (!this.editor || !this.groupDraft) return;

    option.raw = option.raw || {};
    option.raw.nome = option.name;
    option.raw.codigoPdv = option.codigoPdv || '';
    option.raw.descricao = option.description || '';
    option.raw.linkImagem = option.linkImagem || null;
    option.raw.qtdeMaxima = option.qtdeMaxima ?? null;

    this.facade.apliqueEstadoOpcao(this.editor.raw, option.raw, {
      price: Number(option.price || 0),
      active: option.active
    });

    if (option.raw.id) {
      delete option.raw.nova;
      if (!option.raw.removida) {
        option.raw.atualizar = true;
      }
      return;
    }

    option.raw.nova = true;
    delete option.raw.atualizar;
  }

  private removeOption(option: ChoiceOptionVM): void {
    if (!this.podeEditar) {
      this.notificationService.showError('A exclusão de opções está bloqueada neste catálogo compartilhado.');
      return;
    }
    if (!this.groupDraft) return;

    this.syncOptionToRaw(option);
    if (option.raw?.id) {
      option.raw.removida = true;
      delete option.raw.atualizar;
    } else if (this.groupDraft.raw?.opcoesDisponiveis) {
      this.groupDraft.raw.opcoesDisponiveis = this.groupDraft.raw.opcoesDisponiveis
        .filter((rawOption: any) => rawOption !== option.raw);
    }

    this.groupDraft.options = this.groupDraft.options.filter((item) => item !== option);
    this.checkDirty();
    if (this.mode === 'edit') {
      this.scheduleAutoSave();
    }
  }

  private getOptionRuntimeKey(option: ChoiceOptionVM): string {
    const existing = this.optionRuntimeKeys.get(option);
    if (existing) return existing;

    const key = option.id != null ? `option:${option.id}` : `draft:${++this.optionRuntimeKeySequence}`;
    this.optionRuntimeKeys.set(option, key);
    return key;
  }

  private describeDraft(grupo: ChoiceGroupVM): string {
    if (grupo.type === 'single') {
      return `Escolha 1 • ${grupo.options.length} opções`;
    }
    return `Até ${grupo.max} • ${grupo.options.length} opções`;
  }

  private parseOptionMoney(value: string): number {
    const normalized = String(value || '0').replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
    const amount = Number(normalized);
    return Number.isFinite(amount) ? amount : 0;
  }

  private scheduleAutoSave(): void {
    if (this.groupSaveTimer) clearTimeout(this.groupSaveTimer);
    this.groupSaveTimer = setTimeout(async () => {
      if (!this.groupDraft || !this.editor) return;
      try {
        this.groupDraft.description = this.describeDraft(this.groupDraft);
        await this.facade.salveGrupo(this.empresa.catalogo, this.editor.raw, this.groupDraft);
        if (this.groupDraft.kind === 'adicional' && this.groupDraft.raw) {
          this.editor.raw.camposAdicionais = this.editor.raw.camposAdicionais || [];
          const exists = (this.editor.raw.camposAdicionais || []).find((item: any) => item.id === this.groupDraft!.raw?.id);
          if (!exists) {
            this.editor.raw.camposAdicionais.push(this.groupDraft.raw);
          }
        }
      } catch (error: any) {
        this.notificationService.showError(error?.message || 'Não foi possível salvar as opções do adicional.');
      }
    }, 800);
  }
}
